'use client';

import { useTranslations } from 'next-intl';
import type React from 'react';
import { useCallback } from 'react';

import { ProgramDetailVerticalShowingsList } from '@/app/[locale]/(default-layout)/program/[contentId]/_components/ProgramDetailVerticalShowingsList';
import ProgramListCalendar from '@/app/[locale]/(default-layout)/program/_components/ProgramListCalendar';
import ProgramListCalendarBarItem from '@/app/[locale]/(default-layout)/program/_components/ProgramListCalendarBarItem';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { CachedContentData } from '@/hooks/useCachedContentData';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { formatDate_yyyyMMdd } from '@/utils/date-helper';

type ProgramDetailShowingsListProps = {
    selectedDate: string;
    visibleDaysInFuture: number;
    content: CachedContentData;
    fortnightShowings: number[];
    showingsSoonCount: number;
    initialShowings: ShowingDto[];
    initialShowingsUpdatedAt: number;
};
export default function ProgramDetailShowingsGrid(props: ProgramDetailShowingsListProps): React.JSX.Element {
    const {
        visibleDaysInFuture,
        fortnightShowings,
        showingsSoonCount,
        selectedDate,
        content,
        initialShowings,
        initialShowingsUpdatedAt
    } = props;

    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();

    const viewBelowCalendarItem = useCallback(
        (date: Date | 'all' | 'soon', showingsCount: number) => (
            <ProgramDetailVerticalShowingsList
                key={`ProgramDetailVerticalShowingsList#${date}`}
                content={content}
                showingsCount={showingsCount}
                date={typeof date === 'string' && ['soon', 'all'].includes(date) ? date : formatDate_yyyyMMdd(date)}
                initialShowings={initialShowings}
                initialShowingsUpdatedAt={initialShowingsUpdatedAt}
            />
        ),
        [content, initialShowings, initialShowingsUpdatedAt]
    );

    return (
        <div className="flex flex-col space-y-12">
            <ProgramListCalendar
                visibleDaysInFuture={visibleDaysInFuture}
                totalShowingsDaily={fortnightShowings}
                selectedDate={selectedDate}
                onSelectedDateChange={() => null}
                viewBelowCalendarItem={viewBelowCalendarItem}
                isCalendarBarItemAlwaysHighlighted
                widerItemSizeOnMobile
                widthMinSoonItem={false}
                showTrailingItem={{
                    mobile: 'scroll',
                    desktop: 'sticky'
                }}
                trailingItem={
                    <ProgramListCalendarBarItem
                        classNameProgramListCalendarBarItem="box-border relative px-12 uppercase"
                        title={t('common.soon')}
                        isItemActive={selectedDate === 'soon'}
                        isAlwaysHighlighted
                        isDisabled
                        selectedDate={'soon'}
                        widerItemSizeOnMobile
                        onItemClick={() => null}
                        widthMin
                        showingsNumber={{
                            value: showingsSoonCount,
                            isLoading: false
                        }}
                        calendarItemStyle={{
                            backgroundColor: domainConfiguration.style.surfaceColor,
                            backgroundColorItemActive: domainConfiguration.style.primaryColor,
                            textColor: domainConfiguration.style.onSurfaceColor,
                            textColorItemActive: domainConfiguration.style.onPrimaryColor
                        }}
                        viewBelow={() => viewBelowCalendarItem('soon', showingsSoonCount)}
                    />
                }
            />

            <div className="flex flex-wrap"></div>
        </div>
    );
}
